/**
 * Gobierno → Uso de modelos (tenant-local).
 *
 * Muestra el uso **por modelo** del tenant: tokens, peticiones y latencia.
 * Es una superficie de gobierno, no de Model Intelligence: los agentes no se
 * agregan aquí y no se envía nada a CP. Fuente: analytics/usage del DR.
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Area,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Activity, BarChart3, Layers, Timer, Zap } from 'lucide-react';
import { Card, CardContent } from '@/components/ui';
import { AccessDeniedModal } from 'shared/components/errors';
import { useI18n } from '@/i18n';
import { DateRangePicker } from '../components';
import { useAnalyticsAccess, hasAnalyticsAccess } from '../hooks';
import { useUsageDashboard } from '../hooks/useUsageStats';
import type { DateRange } from '../types';

const BRAND = ['#2463eb', '#9333ea', '#06b6d4', '#f59e0b', '#16a34a', '#64748b'];
const RULE = '#e2e8f0';
const MUTED = '#64748b';

function KpiCard({
  icon,
  label,
  value,
  testId,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  testId: string;
}) {
  return (
    <Card data-testid={testId}>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
          {icon}
          {label}
        </div>
        <div className="mt-1 text-2xl font-semibold tabular-nums">{value}</div>
      </CardContent>
    </Card>
  );
}

function ChartCard({
  title,
  icon,
  testId,
  empty,
  children,
}: {
  title: string;
  icon: React.ReactNode;
  testId: string;
  empty?: string;
  children: React.ReactNode;
}) {
  return (
    <Card data-testid={testId}>
      <CardContent className="p-4">
        <div className="mb-3 flex items-center gap-2 text-sm font-medium">
          {icon}
          {title}
        </div>
        {empty ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">{empty}</p>
        ) : (
          <div className="h-72 w-full">{children}</div>
        )}
      </CardContent>
    </Card>
  );
}

export function ModelUsageGovernancePage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [dateRange, setDateRange] = useState<DateRange>({
    from: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
    to: new Date(),
  });
  const { data: accessData, isLoading: accessLoading } = useAnalyticsAccess();
  const allowed = hasAnalyticsAccess(accessData);
  const dashboard = useUsageDashboard(dateRange, null, true, allowed);

  const byModel = dashboard.data?.by_model ?? [];
  const daily = dashboard.data?.daily ?? [];
  const stats = dashboard.data?.stats;

  const totalTokens = (stats?.total_input_tokens ?? 0) + (stats?.total_output_tokens ?? 0);
  const requests = stats?.request_count ?? 0;
  const perRequest = requests > 0 ? totalTokens / requests : null;
  const models = byModel.length;

  const topModels = useMemo(() => byModel.slice(0, 10), [byModel]);
  const requestsModels = useMemo(
    () => [...byModel].sort((a, b) => b.request_count - a.request_count).slice(0, 10),
    [byModel],
  );
  const perRequestModels = useMemo(
    () =>
      byModel
        .map((model) => ({
          ...model,
          tokens_per_request:
            model.request_count > 0 ? model.total_tokens / model.request_count : null,
        }))
        .filter((model) => model.tokens_per_request !== null)
        .sort((a, b) => (b.tokens_per_request ?? 0) - (a.tokens_per_request ?? 0))
        .slice(0, 10),
    [byModel],
  );
  const shareModels = useMemo(() => byModel.slice(0, 6), [byModel]);
  const dailySeries = useMemo(
    () =>
      daily.map((day) => ({
        date: day.date,
        tokens: day.input_tokens + day.output_tokens,
        requests: day.request_count,
      })),
    [daily],
  );

  if (accessLoading) return null;
  if (!allowed) {
    const goHome = () => navigate('/');
    return <AccessDeniedModal isOpen onClose={goHome} onGoBack={goHome} supportEmail="support@atlas.local" />;
  }

  return (
    <div className="space-y-6 p-6" data-testid="model-usage-governance">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <BarChart3 className="h-6 w-6 text-primary" aria-hidden="true" />
          <div>
            <h1 className="text-2xl font-semibold">{t('usageGovernance.title')}</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t('usageGovernance.subtitle')}
            </p>
          </div>
        </div>
        <DateRangePicker value={dateRange} onChange={setDateRange} />
      </div>

      <p className="rounded-md border border-border p-2 text-xs text-gray-500 dark:text-gray-400" data-testid="model-usage-local-note">
        {t('usageGovernance.localNote')}
      </p>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <KpiCard icon={<Layers className="h-4 w-4" aria-hidden="true" />} label={t('usageGovernance.kpiModels')} value={models.toLocaleString()} testId="usage-kpi-models" />
        <KpiCard icon={<Activity className="h-4 w-4" aria-hidden="true" />} label={t('usageGovernance.kpiRequests')} value={requests.toLocaleString()} testId="usage-kpi-requests" />
        <KpiCard icon={<Zap className="h-4 w-4" aria-hidden="true" />} label={t('usageGovernance.kpiTokens')} value={totalTokens.toLocaleString()} testId="usage-kpi-tokens" />
        <KpiCard icon={<BarChart3 className="h-4 w-4" aria-hidden="true" />} label={t('usageGovernance.kpiTokensPerRequest')} value={perRequest !== null ? Math.round(perRequest).toLocaleString() : t('usageGovernance.noData')} testId="usage-kpi-tokens-per-request" />
        <KpiCard icon={<Timer className="h-4 w-4" aria-hidden="true" />} label={t('usageGovernance.kpiLatency')} value={stats?.avg_latency_ms !== undefined ? `${Math.round(stats.avg_latency_ms)} ms` : t('usageGovernance.noData')} testId="usage-kpi-latency" />
      </div>

      {dashboard.isLoading ? (
        <p className="text-sm text-gray-500" data-testid="usage-loading">{t('usageGovernance.loading')}</p>
      ) : dashboard.error ? (
        <p className="text-sm text-gray-500" data-testid="usage-error">{t('usageGovernance.error')}</p>
      ) : byModel.length === 0 ? (
        <Card>
          <CardContent className="p-6 text-sm text-gray-500" data-testid="usage-empty">{t('usageGovernance.empty')}</CardContent>
        </Card>
      ) : (
        <>
          <ChartCard title={t('usageGovernance.dailyTrend')} icon={<Activity className="h-4 w-4" aria-hidden="true" />} testId="usage-chart-daily">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={dailySeries} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={RULE} />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis yAxisId="left" tick={{ fontSize: 11 }} width={64} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} width={48} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Area yAxisId="left" type="monotone" dataKey="tokens" name={t('usageGovernance.kpiTokens')} stroke={BRAND[0]} fill={BRAND[0]} fillOpacity={0.18} />
                <Line yAxisId="right" type="monotone" dataKey="requests" name={t('usageGovernance.kpiRequests')} stroke={BRAND[1]} strokeWidth={2} dot={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </ChartCard>

          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            <ChartCard title={t('usageGovernance.topModels')} icon={<Layers className="h-4 w-4" aria-hidden="true" />} testId="usage-chart-top-models">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={topModels} layout="vertical" margin={{ top: 8, right: 16, bottom: 8, left: 24 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={RULE} />
                  <XAxis type="number" tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="model_id" width={150} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="total_tokens" fill={BRAND[0]} name={t('usageGovernance.kpiTokens')} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title={t('usageGovernance.requestsByModel')} icon={<Activity className="h-4 w-4" aria-hidden="true" />} testId="usage-chart-requests">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={requestsModels} layout="vertical" margin={{ top: 8, right: 16, bottom: 8, left: 24 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={RULE} />
                  <XAxis type="number" tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="model_id" width={150} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="request_count" fill={BRAND[1]} name={t('usageGovernance.kpiRequests')} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>

          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            <ChartCard title={t('usageGovernance.tokensPerRequestByModel')} icon={<BarChart3 className="h-4 w-4" aria-hidden="true" />} testId="usage-chart-tokens-per-request">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={perRequestModels} layout="vertical" margin={{ top: 8, right: 16, bottom: 8, left: 24 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={RULE} />
                  <XAxis type="number" tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="model_id" width={150} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="tokens_per_request" fill={BRAND[2]} name={t('usageGovernance.kpiTokensPerRequest')} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title={t('usageGovernance.shareByModel')} icon={<Layers className="h-4 w-4" aria-hidden="true" />} testId="usage-chart-share">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={shareModels} dataKey="total_tokens" nameKey="model_id" innerRadius="45%" outerRadius="75%" paddingAngle={2}>
                    {shareModels.map((model, index) => (
                      <Cell key={model.model_id} fill={BRAND[index % BRAND.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value: number) => value.toLocaleString()} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>

          <p className="text-xs" style={{ color: MUTED }} data-testid="usage-provenance">
            {t('usageGovernance.provenance')}
          </p>
        </>
      )}
    </div>
  );
}
